import Button from '@/components/base/Button';
import StatusBadge from '@/components/base/StatusBadge';
import { formatMoney } from '@/utils/format';
import type { BulkTransfer } from '@/types/portal';

interface BulkTransferSuccessProps {
  transfer: BulkTransfer;
  onNew: () => void;
}

export default function BulkTransferSuccess({ transfer, onNew }: BulkTransferSuccessProps) {
  return (
    <div className="mx-auto mt-6 w-full max-w-2xl">
      <section className="rounded-lg border border-background-200 bg-background-50">
        <div className="flex flex-col items-center px-6 py-10 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-2xl text-accent-700">
            <i className="ri-check-line" />
          </span>
          <h3 className="mt-4 font-heading text-xl font-semibold text-foreground-950">
            Bulk transfer submitted
          </h3>
          <p className="mt-1 text-sm text-foreground-600">
            {transfer.reference} is {transfer.status.toLowerCase()}.
          </p>

          <div className="mt-6 grid w-full grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-left">
              <p className="text-[11px] font-label font-medium text-foreground-500">Recipients</p>
              <p className="mt-0.5 text-base font-semibold text-foreground-950">
                {transfer.recipientCount}
              </p>
            </div>
            <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-left">
              <p className="text-[11px] font-label font-medium text-foreground-500">Total</p>
              <p className="mt-0.5 text-base font-semibold text-foreground-950">
                {formatMoney(transfer.total, transfer.currency)}
              </p>
            </div>
            <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-left">
              <p className="text-[11px] font-label font-medium text-foreground-500">To creators</p>
              <p className="mt-0.5 text-base font-semibold text-accent-700">
                {formatMoney(transfer.total - transfer.sweep, transfer.currency)}
              </p>
            </div>
            <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-left">
              <p className="text-[11px] font-label font-medium text-foreground-500">To pool</p>
              <p className="mt-0.5 text-base font-semibold text-secondary-900">
                {formatMoney(transfer.sweep, transfer.currency)}
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <StatusBadge status={transfer.status} />
            {transfer.scheduledFor ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary-200 bg-secondary-100 px-3 py-1 text-xs font-label font-medium text-secondary-900">
                <i className="ri-calendar-schedule-line" />
                Goes out {transfer.scheduledFor}
              </span>
            ) : null}
          </div>

          <p className="mt-4 max-w-md text-xs text-foreground-500">
            {transfer.scheduledFor
              ? `Funds are allocated and this batch will go out automatically on ${transfer.scheduledFor}. It can still be cancelled from Transactions before then.`
              : transfer.status === 'Processing'
                ? 'Funds are on their way to each recipient on their settlement route. Track progress in Transactions.'
                : 'The transfer is queued for Finance approval. Funds move once an approver signs off.'}
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button icon="ri-add-line" onClick={onNew}>
              New bulk transfer
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}